import Axios, { AxiosError, AxiosRequestConfig } from 'axios';
import firebase from 'firebase/compat/app';

export class Token {
  static token = '';
}

interface ServerCallResponse<T> {
  data: T;
}

class ServerCall {
  private isRefreshing = false;
  private refreshQueue: ((error: AxiosError | null, token: string | null) => void)[] = [];
  private axiosInstance = Axios.create();

  constructor() {
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        if (error.response?.status === 401 && error.response?.headers['x-token-expired'] === 'true' && !originalRequest._retry) {
          originalRequest._retry = true;

          if (!this.isRefreshing) {
            this.isRefreshing = true;

            try {
              const idToken = (await firebase.auth().currentUser?.getIdToken(true)) as string;
              Token.token = idToken!;
              this.processQueue(null, idToken);
              return this.axiosInstance(originalRequest);
            } catch (refreshError: any) {
              this.processQueue(refreshError, null);
              this.logoutUser();
              return Promise.reject(refreshError);
            } finally {
              this.isRefreshing = false;
            }
          }

          // Queue pending requests until the refresh completes
          return new Promise((resolve, reject) => {
            this.refreshQueue.push((error: AxiosError | null, token: string | null) => {
              if (error) {
                reject(error);
              } else {
                originalRequest.headers['Authorization'] = 'Bearer ' + token;
                resolve(this.axiosInstance(originalRequest));
              }
            });
          });
        }

        return Promise.reject(error);
      }
    );
  }

  private processQueue(error: AxiosError | null, token: string | null) {
    this.refreshQueue.forEach((callback) => callback(error, token));
    this.refreshQueue = [];
  }

  private logoutUser() {
    firebase
      .auth()
      .signOut()
      .then(() => {
        window.location.href = '/login';
      });
  }

  get<T>(url: string, callback: (data: any | null, error: AxiosError | null) => void, isCallBack = false) {
    console.log('GET: ' + url);

    const headers = {
      Authorization: 'Bearer ' + Token.token,
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS'
    };
    const config: AxiosRequestConfig = {
      headers: headers
    };

    this.axiosInstance
      .get<ServerCallResponse<T>>(url, config)
      .then((response) => callback(response.data, null))
      .catch((error: AxiosError) => callback(null, error));
  }

  post<T>(url: string, payload: any, callback: (data: T | null, error: AxiosError | null) => void) {
    console.log('POST: ' + url + '\n' + JSON.stringify(payload));

    const headers = {
      Authorization: 'Bearer ' + Token.token,
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS'
    };

    const config: AxiosRequestConfig = {
      headers: headers
    };

    this.axiosInstance
      .post<ServerCallResponse<T>>(url, payload, config)
      .then((response) => callback(response.data as T, null))
      .catch((error: AxiosError) => callback(null, error));
  }

  postForm<T>(url: string, formData: FormData, callback: (data: T | null, error: AxiosError | null) => void) {
    const headers = {
      Authorization: 'Bearer ' + Token.token,
      'Content-Type': 'multipart/form-data'
    };

    const config: AxiosRequestConfig = { headers };

    this.axiosInstance
      .post<ServerCallResponse<T>>(url, formData, config)
      .then((response) => callback(response.data as T, null))
      .catch((error: AxiosError) => callback(null, error));
  }

  put<T>(url: string, payload: any, callback: (data: T | null, error: AxiosError | null) => void) {
    console.log('PUT: ' + url + '\n' + JSON.stringify(payload));

    const headers = {
      Authorization: 'Bearer ' + Token.token,
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS'
    };

    const config: AxiosRequestConfig = {
      headers: headers
    };

    this.axiosInstance
      .put<ServerCallResponse<T>>(url, payload, config)
      .then((response) => callback(response.data as T, null))
      .catch((error: AxiosError) => callback(null, error));
  }

  delete<T>(url: string, callback: (data: T | null, error: AxiosError | null) => void) {
    console.log('DELETE: ' + url + '\n');

    const headers = {
      Authorization: 'Bearer ' + Token.token,
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS'
    };

    const config: AxiosRequestConfig = {
      headers: headers
    };

    this.axiosInstance
      .delete<ServerCallResponse<T>>(url, config)
      .then((response) => callback(response.data as T, null))
      .catch((error: AxiosError) => callback(null, error));
  }

  download(url: string, fileName: string, callback: (error: AxiosError | null) => void) {
    this.axiosInstance({
      url: url,
      method: 'GET',
      responseType: 'blob',
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: 'Bearer ' + Token.token,
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS'
      }
    })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', response.headers.filename || '');
        document.body.appendChild(link);
        link.click();
        callback(null);
      })
      .catch((error: AxiosError) => callback(error));
  }

  upload(url: string, file: File, callback: (data: any | null, error: AxiosError | null) => void) {
    const formData = new FormData();
    formData.append('file', file);

    const headers = {
      'Content-Type': 'multipart/form-data',
      Authorization: 'Bearer ' + Token.token,
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS'
    };

    const config: AxiosRequestConfig = {
      headers: headers
    };

    this.axiosInstance
      .post<ServerCallResponse<any>>(url, formData, config)
      .then((response) => callback(response.data.data, null))
      .catch((error: AxiosError) => callback(null, error));
  }
}

export default new ServerCall();
